import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceAccessory, PaymentDeviceStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceDtoIn, PaymentDeviceRentalDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { AssignPaymentDeviceDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { MANAGEMENT_DEVICES_URL, paymentDeviceErrorMessage, todayInIsrael } from '@/helpers/paymentDevices';
import { ALL_ACCESSORIES } from '@/helpers/paymentDeviceAccessories';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import AccessoriesField from './AccessoriesField';
import FundPickerField from './FundPickerField';
import DevicePickerField from './DevicePickerField';
import PendingRequestLinkField from './PendingRequestLinkField';
import { DateField, DialogActions, ErrorLine, NoteField } from './dialogParts';

const AVAILABLE_DEVICES_URL = `${MANAGEMENT_DEVICES_URL}?status=${PaymentDeviceStatus.Available}`;

interface AssignDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  /** The device to assign (from the inventory). Without one, the dialog lists the free devices. */
  device?: PaymentDeviceDtoIn | null;
  /** The fund to assign to (from the fund blade). Without one, the dialog asks for it. */
  fundId?: number | null;
  fundName?: string;
}

/**
 * A manual assignment: the office hands a terminal to a fund directly. When that fund is waiting on
 * its own rental request, the dialog offers to answer it with this device, which approves it. "Already handed over" records a rental that began before the system - the rentals that
 * exist today - straight as rented, with its real hand-over date.
 */
const AssignDeviceDialog = ({ open, onClose, device = null, fundId = null, fundName }: AssignDeviceDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const today = todayInIsrael();

  const { data: availableDevices, isLoading: isLoadingDevices } = useGetAuthApi<PaymentDeviceDtoIn[]>(open && !device ? AVAILABLE_DEVICES_URL : '');

  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(today);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [alreadyHandedOver, setAlreadyHandedOver] = useState(false);
  const [handedOverDate, setHandedOverDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [requestId, setRequestId] = useState<number | null>(null);
  const [accessories, setAccessories] = useState<PaymentDeviceAccessory[]>(ALL_ACCESSORIES);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeviceId(device?.id ?? null);
    setSelectedFundId(fundId);
    setStartDate(today);
    setExpectedReturnDate('');
    setAlreadyHandedOver(false);
    setHandedOverDate(today);
    setNotes('');
    setAccessories(ALL_ACCESSORIES);
    // requestId is left to PendingRequestLinkField: it picks the fund's oldest pending request (or
    // none) itself, and a reset here would run after it and undo that.
    setError(null);
  }, [open, device, fundId, today]);

  const handleSubmit = async () => {
    if (deviceId == null) {
      setError(t('paymentDevices.dialogs.deviceRequired'));
      return;
    }
    if (selectedFundId == null) {
      setError(t('paymentDevices.dialogs.fundRequired'));
      return;
    }
    if (!startDate || !expectedReturnDate) {
      setError(t('paymentDevices.dialogs.datesRequired'));
      return;
    }
    if (expectedReturnDate <= startDate) {
      setError(t('paymentDevices.dialogs.returnAfterStart'));
      return;
    }
    if (alreadyHandedOver && !handedOverDate) {
      setError(t('paymentDevices.dialogs.handedOverDateRequired'));
      return;
    }
    if (alreadyHandedOver && handedOverDate > today) {
      setError(t('paymentDevices.dialogs.dateNotInFuture'));
      return;
    }

    const body: AssignPaymentDeviceDtoOut = {
      fundId: selectedFundId,
      startDate,
      expectedReturnDate,
      alreadyHandedOver,
      handedOverDate: alreadyHandedOver ? handedOverDate : null,
      notes: notes.trim() || null,
      requestId,
      accessories: alreadyHandedOver ? accessories : null,
    };
    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<PaymentDeviceRentalDtoIn, AssignPaymentDeviceDtoOut>(`${MANAGEMENT_DEVICES_URL}/${deviceId}/assign`, body);
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const devices = availableDevices ?? [];

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={device ? t('paymentDevices.dialogs.assignTitle', { number: device.deviceNumber }) : t('paymentDevices.dialogs.assignDeviceTitle')}
      maxWidth="560px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        {!device && (
          <DevicePickerField
            id="pd-assign-device"
            label={t('paymentDevices.dialogs.chooseDevice')}
            devices={devices}
            value={deviceId}
            onChange={setDeviceId}
            isLoading={isLoadingDevices}
          />
        )}

        <FundPickerField
          id="pd-assign-fund"
          label={t('paymentDevices.dialogs.fund')}
          value={selectedFundId}
          onChange={setSelectedFundId}
          locked={fundId != null}
          fallbackName={fundName}
        />

        <PendingRequestLinkField fundId={selectedFundId} value={requestId} onChange={setRequestId} />

        <div className="flex gap-4">
          <DateField id="pd-assign-start" label={t('paymentDevices.dialogs.startDate')} value={startDate} onChange={setStartDate} />
          <DateField
            id="pd-assign-return"
            label={t('paymentDevices.dialogs.expectedReturnDate')}
            value={expectedReturnDate}
            onChange={setExpectedReturnDate}
            min={startDate || undefined}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer body-sm-regular">
          <Checkbox checked={alreadyHandedOver} onCheckedChange={setAlreadyHandedOver} />
          {t('paymentDevices.dialogs.alreadyHandedOver')}
        </label>
        {alreadyHandedOver && (
          <DateField
            id="pd-assign-handover"
            label={t('paymentDevices.dialogs.handedOverDate')}
            value={handedOverDate}
            onChange={setHandedOverDate}
            max={today}
          />
        )}
        {alreadyHandedOver && (
          <AccessoriesField
            label={t('paymentDevices.accessories.handedOverLabel')}
            options={ALL_ACCESSORIES}
            value={accessories}
            onChange={setAccessories}
          />
        )}

        <NoteField id="pd-assign-notes" label={t('paymentDevices.dialogs.notesOptional')} value={notes} onChange={setNotes} />
        <ErrorLine error={error} />
        <DialogActions onCancel={onClose} onConfirm={handleSubmit} isSubmitting={isSubmitting} confirmText={t('paymentDevices.actions.assign')} />
      </div>
    </MainDialogLayout>
  );
};

export default AssignDeviceDialog;
